import { ApolloClient } from '@apollo/client'
import { Terminal } from '@stripe/terminal-js'
import VendNumber from 'vend-number'
import { ApiRetailerPaymentType, ApiTransaction } from '@vend/types'
import { TerminalEventMonitoring } from '@lightspeed-payments/payments-terminal-sdk'
import {
  LSPAYErrorMessage,
  Register,
  SwiperError,
} from '../../helpers/lspay.types'
import { RefundMethod } from '../../helpers/refund'
import { PairingState } from '../use-payment-effect-reducer/types'
import { TimerStarterFunction } from '../timeTracking'
import { ErrorTrackingFunction } from '../errorTracking'

export enum UnrefRefundReason {
  ZERO_DOLLAR_REFUNDABLE = 'ZERO_DOLLAR_REFUNDABLE',
  MULTIPLE_PAYMENTS = 'MULTIPLE_PAYMENTS',
  BLIND_REFUND = 'BLIND_REFUND',
  DIFFERENT_PAYMENT_TYPES = 'DIFFERENT_PAYMENT_TYPES',
}

export type RefundState =
  | {
      status: 'refundingViaCardNotPresent'
      retailerPaymentType: ApiRetailerPaymentType
      retryable?: boolean
      dismissible?: false
    }
  | {
      status: 'interacPresentPrompt'
      retailerPaymentType: ApiRetailerPaymentType
      registerId: string
      chargeId: string
      amount: VendNumber
      retryable?: false
      dismissible: true
    }
  | {
      status: 'amountExceededPrompt'
      retailerPaymentType: ApiRetailerPaymentType
      amount: VendNumber
      refundableAmount: string
      retryable?: false
      dismissible: true
    }
  | {
      status: 'unreferencedPrompt'
      retailerPaymentType: ApiRetailerPaymentType
      amount: VendNumber
      unrefReason: UnrefRefundReason
      retryable?: false
      dismissible: true
    }
  | {
      status: 'fetchingPairing'
      retailerPaymentType: ApiRetailerPaymentType
      refundMethod: RefundMethod
      amount: VendNumber
      retryable?: boolean
      dismissible?: false
    }
  | {
      status: 'connectingToTerminal'
      retailerPaymentType: ApiRetailerPaymentType
      pairing: PairingState
      refundMethod: RefundMethod
      amount: VendNumber
      retryable?: boolean
      dismissible?: false
    }
  | {
      status: 'readingReusableCard'
      retailerPaymentType: ApiRetailerPaymentType
      terminal: Terminal
      terminalMonitoringInstance: TerminalEventMonitoring
      amount: VendNumber
      refundMethod: RefundMethod
      retryable?: boolean
      dismissible?: false
    }
  | {
      status: 'creatingSetupIntent'
      retailerPaymentType: ApiRetailerPaymentType
      terminal: Terminal
      terminalMonitoringInstance: TerminalEventMonitoring
      amount: VendNumber
      refundMethod: RefundMethod
      retryable?: boolean
      dismissible?: false
    }
  | {
      status: 'collectingSetupIntentPaymentMethod'
      retailerPaymentType: ApiRetailerPaymentType
      amount: VendNumber
      refundMethod: RefundMethod
      clientSecret: string
      terminal: Terminal
      terminalMonitoringInstance: TerminalEventMonitoring
      retryable?: boolean
      dismissible?: false
    }
  | {
      status: 'performingUnreferencedRefund'
      retailerPaymentType: ApiRetailerPaymentType
      paymentMethodId: string
      amount: VendNumber
      retryable?: boolean
      dismissible?: false
    }
  | {
      status: 'refundingViaCardPresent'
      retailerPaymentType: ApiRetailerPaymentType
      refundMethod: RefundMethod
      chargeId: string
      amount: VendNumber
      terminal: Terminal
      terminalMonitoringInstance: TerminalEventMonitoring
      retryable?: boolean
      dismissible?: false
    }
  | {
      status: 'persistingCardPresentRefund'
      retailerPaymentType: ApiRetailerPaymentType
      refundMethod: RefundMethod
      amount: VendNumber
      retryable?: boolean
      dismissible?: false
    }
  | {
      status: 'done'
      transaction: ApiTransaction
      retryable?: false
      dismissible?: false
    }
  | {
      status: 'error'
      errorMessage: LSPAYErrorMessage
      retryable: boolean
      dismissible: true
      previousState: RefundState
    }

export type RefundEvent =
  | { type: 'RETRY' }
  | {
      type: 'CARD_NOT_PRESENT_FAIL'
      error: SwiperError
      registerId: string
      amount: VendNumber
    }
  | { type: 'ERROR'; errorMessage: LSPAYErrorMessage; retryable: boolean }
  | { type: 'CONTINUE_WITH_CARD_PRESENT' }
  | { type: 'CONTINUE_WITH_UNREFERENCED' }
  | { type: 'REFUND_ORIGINAL_AMOUNT'; amount: VendNumber }
  | { type: 'PAIRING_OK'; pairing: PairingState }
  | {
      type: 'TERMINAL_OK'
      terminal: Terminal
      terminalMonitoringInstance: TerminalEventMonitoring
      currency: string
    }
  | { type: 'CREATE_SETUP_INTENT_PAYMENT_METHOD_OK'; clientSecret: string }
  | {
      type: 'PAYMENT_METHOD_OK'
      paymentMethodId: string
    }
  | { type: 'CARD_PRESENT_OK' }
  | { type: 'COMPLETE_REFUND'; transaction: ApiTransaction }

export type RefundEffect =
  | { type: 'cardNotPresentEffect'; overrideAmount?: VendNumber }
  | { type: 'fetchPairingEffect' }
  | {
      type: 'connectTerminalEffect'
      pairingSN: string
      isSimulated: boolean
      terminalID?: string
      terminalModel?: string
    }
  | {
      type: 'readReusableCardEffect'
      terminal: Terminal
      terminalMonitoringInstance: TerminalEventMonitoring
      isRetry: boolean
    }
  | { type: 'createSetupIntentEffect' }
  | {
      type: 'collectSetupIntentPaymentMethodEffect'
      terminal: Terminal
      terminalMonitoringInstance: TerminalEventMonitoring
      clientSecret: string
      isRetry: boolean
    }
  | {
      type: 'unreferencedRefundEffect'
      paymentMethodId: string
      amount: VendNumber
    }
  | {
      type: 'cardPresentEffect'
      terminal: Terminal
      terminalMonitoringInstance: TerminalEventMonitoring
      chargeId: string
      amount: VendNumber
      isRetry: boolean
    }
  | {
      type: 'persistCardPresentEffect'
      refundMethod: RefundMethod
      amount: VendNumber
    }

export interface RefundReducerArgs {
  amount: VendNumber
  currency: string
  outletId: string
  originalRegisterSalePaymentIds?: string[]
  originalSalePaidByOtherPT?: boolean
  originalSaleHasProcessingServicePayment?: boolean
  // Total amount from the original LSPay payment(s) for split-tender detection
  originalLspayPaymentAmount?: number
  transactionId: string
  retailerPaymentType: ApiRetailerPaymentType
  register: Register
}

export interface RefundEffectsMapArgs extends RefundReducerArgs {
  trackError: ErrorTrackingFunction
  trackTiming: TimerStarterFunction
  isSimulated: boolean
  apolloClient: ApolloClient<object>
  isSandbox: boolean
}
